import { type AxiosPromise, isAxiosError, isCancel } from 'axios';
import { type Ref, type ShallowRef, ref, shallowRef } from 'vue';

import type { ApiResponse } from '@abaninja/types';

import { type CancellablePromise } from './createCancellablePromise';
import { useLoadable } from './useLoadable';
import { useMeta } from './useMeta';
import { useStatusCode } from './useStatusCode';

// eslint-disable-next-line max-lines-per-function
export const useGetEntity = <TArgs extends unknown[], TData, TMeta = never, Shallow extends boolean = true>(
  handler: (...args: TArgs) => CancellablePromise<AxiosPromise<ApiResponse<TData, TMeta>>>,
  initialMeta?: TMeta,
  initialState?: TData,
  options?: {
    shallow: Shallow;
  },
) => {
  const { shallow = true } = options ?? {};

  const state = shallow ? shallowRef(initialState) : ref(initialState);

  function resetState() {
    state.value = initialState;
  }

  const { abortController, cancel, hasLoaded, isLoading, reset: resetLoadable } = useLoadable();
  const { meta, reset: resetMeta } = useMeta<TMeta>(initialMeta);
  const { reset: resetStatusCode, statusCode } = useStatusCode();

  // eslint-disable-next-line complexity
  async function getEntity(...args: TArgs) {
    cancel();

    const { abortController: controller, promise } = handler(...args);

    abortController.value = controller;

    try {
      isLoading.value = true;

      const response = await promise();

      if (typeof response.data === 'object' && 'data' in response.data) {
        state.value = response.data.data;
      }

      if (typeof response.data === 'object' && 'meta' in response.data) {
        meta.value = response.data.meta;
      }

      statusCode.value = response.status;
      hasLoaded.value = true;
      isLoading.value = false;

      return response;
    } catch (error) {
      if (!isCancel(error)) {
        isLoading.value = false;
      }

      if (isAxiosError<ApiResponse<TData, TMeta>>(error)) {
        // eslint-disable-next-line max-depth
        if (error.response && 'meta' in error.response.data) {
          meta.value = error.response.data.meta;
        }

        statusCode.value = error.status;
      }

      throw error;
    }
  }

  async function requireEntity(...args: TArgs) {
    if (!isLoading.value && !hasLoaded.value) {
      await getEntity(...args);
    }

    return true;
  }

  function reset() {
    resetLoadable();
    resetState();
    resetStatusCode();
    resetMeta();
  }

  return {
    cancel,
    entity: state as Shallow extends true ? ShallowRef<TData | undefined> : Ref<TData | undefined>,
    getEntity,
    hasLoaded,
    isLoading,
    meta,
    requireEntity,
    reset,
    statusCode,
  };
};
